import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { creditCardLookup } from '../../../services/requests/orders/orders-requests';
import { OrderType } from './orders-types';

interface CreditCardLookup {
  cst?: string;
  month?: string;
  year?: string;
  cardHolderName?: string;
  cardType?: string;
  warning?: string;
}

/**
 * Dewar credit-card status panel — mirrors the legacy `GetCreditCardStatus()`
 * on `HeliumOrders.aspx.cs`. Only rendered for Dewar orders; it calls the
 * existing Billing domain lookup (`/DewarBilling/CreditCardLookup`) with the
 * order's customer id and surfaces the card holder / type / expiry, or the
 * "no credit card assigned" message, plus any expiry warning.
 */
export const OrderCreditCardStatus: React.FC<any> = ({ watch }) => {
  // Prefer the live form state (works on both the edit and create pages) and
  // fall back to the loaded order in redux when no form watcher is supplied.
  const reduxOrderTypeId = useSelector((state: any) => state.orders?.current?.data?.orderTypeId);
  const reduxCustomerId = useSelector((state: any) => state.orders?.current?.data?.customerId);
  const orderTypeId = watch ? watch('orderTypeId') : reduxOrderTypeId;
  const customerId = watch ? watch('customerId') : reduxCustomerId;
  const [status, setStatus] = useState<CreditCardLookup | null>(null);
  const [loading, setLoading] = useState(false);

  const isDewar = Number(orderTypeId) === OrderType.Dewar;

  useEffect(() => {
    if (!isDewar || !customerId) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    creditCardLookup(customerId)
      .then((data: CreditCardLookup) => {
        if (!cancelled) setStatus(data ?? null);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDewar, customerId]);

  if (!isDewar) return null;

  const label = <div className="form-label fw-bold mb-1">Credit Card Status</div>;

  if (loading) {
    return (
      <div className="mb-2">
        {label}
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Checking credit card…</span>
        </div>
      </div>
    );
  }

  const cst = status?.cst ?? String(customerId ?? '');
  const hasCard = !!status?.cardHolderName;

  return (
    <div className="mb-2">
      {label}
      {status?.warning ? (
        <div className="text-danger fw-bold">{status.warning}</div>
      ) : hasCard ? (
        <div>
          <div>Credit Card Found for {cst}</div>
          <div>{status?.cardHolderName}</div>
          <div>{status?.cardType}</div>
          <div>
            Expires: {status?.month}/{status?.year}
          </div>
        </div>
      ) : (
        <div className="text-muted">There is no credit card assigned to CST {cst}</div>
      )}
    </div>
  );
};

export default OrderCreditCardStatus;
